<template>
  <label
    :class="['base-label', { required: required }]"
    :for="htmlFor"
  >
    <slot />
  </label>
</template>

<script setup lang="ts">
/**
 * Form label component with required field indicator
 *
 * Provides styled label with optional red asterisk for required fields.
 * Can be used with form inputs (via htmlFor) or as fieldset legend.
 * Automatically used by FormControl component.
 *
 * @example Label for input
 * ```vue
 * <BaseLabel htmlFor="email-input" required>
 *   Email Address
 * </BaseLabel>
 * <BaseInput id="email-input" v-model="email" type="email" />
 * ```
 *
 * @example Fieldset legend
 * ```vue
 * <BaseFieldset>
 *   <BaseLabel>Contact Preferences</BaseLabel>
 *   <BaseCheckbox v-model="emailOpt" label="Email updates" />
 *   <BaseCheckbox v-model="smsOpt" label="SMS notifications" />
 * </BaseFieldset>
 * ```
 *
 * @example Standalone label without for
 * ```vue
 * <BaseLabel required>
 *   Select Payment Method
 * </BaseLabel>
 * <BaseRadioGroup
 *   v-model="payment"
 *   name="payment"
 *   :options="paymentOptions"
 * />
 * ```
 */
export interface BaseLabelProps {
  /**
   * ID of the input this label is associated with
   * Creates accessible label-input relationship
   * @default undefined
   */
  htmlFor?: string;

  /**
   * Displays red asterisk indicator for required fields
   * @default false
   */
  required?: boolean;
}

withDefaults(defineProps<BaseLabelProps>(), {
  required: false,
});
</script>

<style>
.base-label {
  display: block;
  font-weight: var(--input-label-weight);
  font-size: var(--font-md);
  line-height: 1.5;
  margin: 0;
  color: var(--input-label-color);
  &.required::after {
    content: var(--input-label-required);
    color: var(--color-red-300);
  }
  &:not(.required)::after {
    content: var(--input-label-optional);
    color: var(--color-neutral-600);
  }
}
</style>
